import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { apiClient } from "../sdk/client";
import type { UserContext as User, UserType } from "./types";

interface AuthState {
  user: User | null;
  accessToken: string | null;
  loading: boolean;
}

interface AuthContextValue extends AuthState {
  login: (email: string, senha: string) => Promise<void>;
  logout: () => void;
  /** Re-sincroniza o status estendido (após trocar senha, completar cadastro, etc). */
  refreshStatus: () => Promise<void>;
}

/**
 * Faz GET /auth/me + /auth/me/status e mescla — o status traz as flags do
 * wizard de primeiro acesso que não cabem no JWT.
 */
async function loadUser(token: string): Promise<User> {
  const [base, status] = await Promise.all([
    apiClient.get<User>("/auth/me", { token }),
    apiClient.get<Partial<User>>("/auth/me/status", { token }).catch(() => ({} as Partial<User>)),
  ]);
  // /auth/me devolve `id`; o resto do app (e o JWT) usa `userId` — normaliza aqui.
  const id = (base as User & { id?: string }).id;
  return { ...base, ...status, userId: base.userId ?? id ?? "" };
}

const AuthContext = createContext<AuthContextValue | null>(null);
const TOKEN_KEY = "pro-roller.access_token";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    return {
      user: null,
      accessToken: token,
      // Se há token salvo, partimos em "loading" pra ProtectedRoute esperar o
      // /me antes de decidir se redireciona pra /login.
      loading: !!token,
    };
  });

  // Bootstrap: se temos token salvo, busca o /me.
  useEffect(() => {
    let cancelled = false;
    const token = state.accessToken;
    if (!token) return;
    loadUser(token)
      .then((user) => {
        if (!cancelled) setState({ user, accessToken: token, loading: false });
      })
      .catch(() => {
        if (!cancelled) {
          localStorage.removeItem(TOKEN_KEY);
          setState({ user: null, accessToken: null, loading: false });
        }
      });
    return () => {
      cancelled = true;
    };
    // só roda no mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Só em dev (localhost): mostra o usuário logado no título da guia — ajuda a
  // distinguir várias abas abertas ao mesmo tempo (juiz 1, operador, etc).
  useEffect(() => {
    const host = window.location.hostname;
    if (host !== "localhost" && host !== "127.0.0.1") return;
    // O Safari, com muitas abas, esconde o prefixo comum e corta o título em limites de
    // palavra (some "Pro Roller - "). Deixando o título inteiro como um bloco só (todos os
    // espaços não-quebráveis,  ) ele não tem onde cortar por palavra e mostra desde o início.
    const nome = state.user?.nomeCompleto?.trim();
    const titulo = nome ? `Pro Roller - ${nome}` : "Pro Roller - Plataforma de Patinação Artística";
    document.title = titulo.replace(/ /g, " ");
  }, [state.user]);

  const login = useCallback(async (email: string, senha: string) => {
    const res = await apiClient.post<{ accessToken: string }>("/auth/login", { email, senha });
    localStorage.setItem(TOKEN_KEY, res.accessToken);
    const user = await loadUser(res.accessToken);
    setState({ user, accessToken: res.accessToken, loading: false });
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setState({ user: null, accessToken: null, loading: false });
  }, []);

  const refreshStatus = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    const user = await loadUser(token);
    setState((s) => ({ ...s, user, accessToken: token, loading: false }));
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, login, logout, refreshStatus }),
    [state, login, logout, refreshStatus],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth precisa estar dentro de <AuthProvider>");
  return ctx;
}

export type { UserType };
